import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { toValue } from '@vueuse/core';
import { set } from '@/utils/reactive-object.ts';
import uniqueId from '@/utils/unique-id.ts';

const stack = ref({});

function getCurrent(namespace) {
    return (stack.value?.[namespace] ?? []).length ? stack.value[namespace][stack.value[namespace].length - 1] : undefined;
}

function add(id, zIndexBase, namespace) {
    const current = getCurrent(namespace);

    set(stack.value, namespace, [
        ...(stack.value?.[namespace] ?? []),
        {
            id,
            zi: (current?.zi ?? zIndexBase) + (current?.zi ? 1 : 0),
        },
    ]);
}

function remove(id, namespace) {
    const index = (stack.value?.[namespace] ?? []).findIndex(i => i.id === id);

    if (index === -1) return;

    set(stack.value, namespace, stack.value[namespace].toSpliced(index, 1));
}

export function useStack(display, zIndexBase = 20200, namespace = 'default') {
    const id = uniqueId();

    const isActive = computed(() => getCurrent(namespace)?.id === id);
    const stackIndex = computed(() => (stack.value?.[namespace] ?? []).findIndex(i => i.id === id));

    const zIndex = computed(() => (stack.value?.[namespace] ?? [])?.[stackIndex.value]?.zi);

    const size = computed(() => (stack.value?.[namespace] ?? []).length);

    watch(() => toValue(display), () => {
        toValue(display) ? add(id, zIndexBase, namespace) : remove(id, namespace);
    }, { immediate: true });

    onBeforeUnmount(() => {
        remove(id, namespace);
    });

    return {
        id,
        isActive,
        stackIndex,
        zIndex,
        size,
    };
}
